'use client';
import { authHref, safeAuthReturn } from '@/shared/lib/auth-return';

import { useActionState, useEffect } from 'react';

import { useToast } from '@/features/toast';
import { Button, LockIcon, OptIn, TextFiled } from '@/shared';

import { restoreAction, restoreConfirmAction } from '../action';
import { useResendCodeAction } from '../hooks';
import { ResendCode } from './resend-code';

type RestoreConfirmFormProps = {
	redirectPath?: string;
	email: string;
	action?: typeof restoreConfirmAction;
	resendAction?: typeof restoreAction;
	onSuccess?: () => void;
};

export function RestoreConfirmForm({
	email,
	action = restoreConfirmAction,
	resendAction: resend = restoreAction,
	onSuccess,
	redirectPath,
}: RestoreConfirmFormProps) {
	const { addToast } = useToast();
	const [state, formAction, pending] = useActionState(action, {});
	const [resendKey, resendAction, resending] = useResendCodeAction(resend, {
		email,
		redirect: safeAuthReturn(redirectPath) ?? '',
	});

	useEffect(() => {
		if (state.message) {
			addToast('Ошибка', state.message, 'error');
		}
	}, [addToast, state]);

	useEffect(() => {
		if (state.success) {
			onSuccess?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.success]);

	return (
		<form
			className="flex flex-1 flex-col gap-8 md:flex-grow-0"
			action={formAction}
		>
			<input
				type="hidden"
				name="redirect"
				value={safeAuthReturn(redirectPath) ?? ''}
			/>
			<fieldset>
				<TextFiled type="hidden" name="email" value={email} />

				<OptIn
					label="Код верификации, присланный на email"
					name="verificationCode"
					hint={
						<ResendCode
							key={resendKey}
							loading={resending}
							onClick={resendAction}
						/>
					}
					defaultValue={
						(state.formData?.get('verificationCode') || '') as string
					}
					size="lg"
					invalid={!!state?.errors?.verificationCode}
					autoComplete="one-time-code"
				/>
			</fieldset>

			<fieldset className="mb-auto space-y-3">
				<TextFiled
					type="password"
					placeholder="Новый пароль"
					name="password"
					autoComplete="new-password"
					icon={<LockIcon className="size-6" />}
					defaultValue={(state.formData?.get('password') || '') as string}
					invalid={!!state?.errors?.password}
					hint={state?.errors?.password?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
				<TextFiled
					type="password"
					placeholder="Повторите новый пароль"
					name="confirmPassword"
					autoComplete="new-password"
					icon={<LockIcon className="size-6" />}
					defaultValue={
						(state.formData?.get('confirmPassword') || '') as string
					}
					invalid={!!state?.errors?.confirmPassword}
					hint={state?.errors?.confirmPassword?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
			</fieldset>
			<Button type="submit" loading={pending} block>
				Продолжить
			</Button>
		</form>
	);
}
